/**
 * Admin: run auto-detect on every selected emoji in one pass.
 *
 * Flow:
 *   1. Admin multi-selects N emojis in the grid and opens this dialog.
 *   2. We call /auto-detect-bad-platforms for each one in parallel
 *      (Promise.all). Progress meter updates as responses land.
 *   3. Results panel shows per-emoji rows with the platforms each one
 *      would have flagged. Admin can uncheck specific emojis OR specific
 *      platform suggestions before applying.
 *   4. "Apply" fires a PUT per emoji to merge the approved suggestions
 *      into each emoji's excluded_platforms array. Local grid updates
 *      via onApplied callback so the admin sees the updated badges
 *      immediately.
 *
 * The summary is the entire point of this dialog — running the heuristic
 * one-emoji-at-a-time would defeat the purpose. The per-row checkbox
 * controls let the admin override individual suggestions before commit.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Loader2, Wand2, X, Check, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const BatchAutoDetectDialog = ({ open, onOpenChange, emojis, onApplied }) => {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);  // 0..emojis.length
  const [results, setResults] = useState({});   // {emoji_id: {suggested: [...], scored: [...], error}}
  // Per-emoji approval: true = include in apply, false = skip this row.
  // We default every result row to approved.
  const [approved, setApproved] = useState({}); // {emoji_id: bool}
  const [applying, setApplying] = useState(false);
  const [labels, setLabels] = useState({});     // {platform_key: human label}

  // Reset state via the close handler — keeps the lint happy and is
  // semantically clearer than mutating multiple states inside a useEffect.
  const handleOpenChange = (newOpen) => {
    if (!newOpen) {
      setRunning(false);
      setProgress(0);
      setResults({});
      setApproved({});
    }
    onOpenChange(newOpen);
  };

  // Load the platform labels lookup the first time the dialog opens —
  // small payload, cached for the dialog lifetime so we don't re-fetch
  // every Run.
  useEffect(() => {
    if (!open || Object.keys(labels).length > 0) return undefined;
    let cancelled = false;
    (async () => {
      const { data } = await apiFetch('/api/emojis/platform-specs');
      if (cancelled) return;
      const m = {};
      (data?.platforms || []).forEach((p) => { m[p.key] = p.label; });
      setLabels(m);
    })();
    return () => { cancelled = true; };
  }, [open, labels]);

  const runBatch = async () => {
    if (!emojis || emojis.length === 0) return;
    setRunning(true);
    setProgress(0);
    const collected = {};
    const approval = {};
    let done = 0;
    // Promise.allSettled so a single 404/500 doesn't kill the batch.
    await Promise.allSettled(emojis.map(async (e) => {
      try {
        const { data, error } = await apiFetch(
          `/api/admin/emojis/${e.emoji_id}/auto-detect-bad-platforms`,
          { method: 'POST', body: {} },
        );
        if (error) throw new Error(error);
        collected[e.emoji_id] = {
          suggested: data?.suggested_exclusions || [],
          scored: data?.scored || [],
          error: null,
        };
        // Default-approve every result that actually has suggestions.
        approval[e.emoji_id] = (data?.suggested_exclusions || []).length > 0;
      } catch (err) {
        collected[e.emoji_id] = { suggested: [], scored: [], error: err.message || 'Failed' };
        approval[e.emoji_id] = false;
      } finally {
        done += 1;
        setProgress(done);
      }
    }));
    setResults(collected);
    setApproved(approval);
    setRunning(false);
  };

  const approvedWithSuggestions = useMemo(() => {
    return emojis.filter((e) => approved[e.emoji_id] && (results[e.emoji_id]?.suggested || []).length > 0);
  }, [emojis, approved, results]);

  const apply = async () => {
    if (approvedWithSuggestions.length === 0) {
      toast.info('No emojis approved — toggle on the rows you want to update.');
      return;
    }
    setApplying(true);
    const updates = {};  // {emoji_id: merged excluded_platforms}
    let ok = 0;
    let failed = 0;
    await Promise.allSettled(approvedWithSuggestions.map(async (e) => {
      const suggestions = results[e.emoji_id]?.suggested || [];
      const merged = Array.from(new Set([...(e.excluded_platforms || []), ...suggestions]));
      const { error } = await apiFetch(`/api/admin/emojis/${e.emoji_id}`, {
        method: 'PUT',
        body: { excluded_platforms: merged },
      });
      if (error) { failed += 1; return; }
      ok += 1;
      updates[e.emoji_id] = merged;
    }));
    setApplying(false);
    if (ok > 0) toast.success(`Updated ${ok} emoji(s) with new exclusions.`);
    if (failed > 0) toast.error(`${failed} update(s) failed.`);
    if (Object.keys(updates).length > 0) onApplied?.(updates);
    handleOpenChange(false);
  };

  const hasResults = Object.keys(results).length > 0;
  const totalFlagged = Object.values(results).reduce(
    (sum, r) => sum + (r.suggested?.length || 0), 0,
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="batch-autodetect-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-purple-600 dark:text-purple-300" />
            Batch auto-detect bad platforms
          </DialogTitle>
          <DialogDescription>
            Runs the edge-density &amp; contrast-loss heuristic on every selected emoji in parallel. Review the suggestions, toggle off any rows you disagree with, then apply.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 px-1">
          {!hasResults && !running && (
            <div className="text-center py-8 space-y-3">
              <p className="text-sm text-muted-foreground">
                {emojis.length} emoji{emojis.length === 1 ? '' : 's'} selected.
              </p>
              <Button
                onClick={runBatch}
                className="bg-purple-600 text-white hover:bg-purple-700"
                disabled={emojis.length === 0}
                data-testid="batch-autodetect-run-btn"
              >
                <Wand2 className="h-4 w-4 mr-1" /> Run auto-detect on {emojis.length}
              </Button>
            </div>
          )}

          {running && (
            <div className="text-center py-8 space-y-2">
              <Loader2 className="h-8 w-8 mx-auto animate-spin text-purple-600" />
              <p className="text-sm">Analysing {progress} / {emojis.length} emojis…</p>
              <div className="w-full max-w-md mx-auto bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="bg-purple-500 h-full transition-all"
                  style={{ width: `${(progress / emojis.length) * 100}%` }}
                />
              </div>
            </div>
          )}

          {hasResults && !running && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2 p-3 rounded-md bg-purple-500/5 border border-purple-500/30">
                <div className="text-sm">
                  <Badge className="bg-purple-600 text-white mr-2">{totalFlagged}</Badge>
                  platform suggestion{totalFlagged === 1 ? '' : 's'} across {emojis.length} emoji{emojis.length === 1 ? '' : 's'}.
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={runBatch}
                  disabled={running}
                  data-testid="batch-autodetect-rerun-btn"
                >
                  Re-run
                </Button>
              </div>

              <div className="space-y-1.5" data-testid="batch-autodetect-results">
                {emojis.map((e) => {
                  const r = results[e.emoji_id];
                  if (!r) return null;
                  const hasSuggestions = r.suggested.length > 0;
                  const isApproved = approved[e.emoji_id];
                  return (
                    <div
                      key={e.emoji_id}
                      className={`flex gap-2 items-start p-2 rounded border text-xs ${
                        r.error
                          ? 'border-red-500/30 bg-red-500/5'
                          : !hasSuggestions
                            ? 'border-green-500/30 bg-green-500/5'
                            : isApproved
                              ? 'border-amber-500/40 bg-amber-500/10'
                              : 'border-border bg-muted/30'
                      }`}
                      data-testid={`batch-autodetect-row-${e.emoji_id}`}
                    >
                      {/* Approval checkbox — only meaningful when there are
                          suggestions to apply. */}
                      <label className="pt-1 shrink-0">
                        <input
                          type="checkbox"
                          checked={!!isApproved && hasSuggestions}
                          disabled={!hasSuggestions || !!r.error}
                          onChange={(ev) => setApproved((prev) => ({
                            ...prev, [e.emoji_id]: ev.target.checked,
                          }))}
                          className="h-3.5 w-3.5"
                          data-testid={`batch-autodetect-approve-${e.emoji_id}`}
                        />
                      </label>
                      {/* Thumbnail */}
                      <div className="w-10 h-10 shrink-0 bg-background/50 rounded border flex items-center justify-center overflow-hidden">
                        {e.image_path && (
                          <img
                            src={e.image_path?.startsWith('http') ? e.image_path : `${API}${e.image_path}`}
                            alt={e.label}
                            className="w-full h-full object-contain"
                          />
                        )}
                      </div>
                      {/* Body */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium truncate" title={e.label}>{e.label}</p>
                          {r.error ? (
                            <Badge variant="outline" className="text-[10px] border-red-500/40 text-red-600">
                              <AlertTriangle className="h-2.5 w-2.5 mr-1" /> {r.error}
                            </Badge>
                          ) : !hasSuggestions ? (
                            <Badge variant="outline" className="text-[10px] border-green-500/40 text-green-700 dark:text-green-300">
                              <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> Reads well everywhere
                            </Badge>
                          ) : (
                            <Badge className="text-[10px] bg-amber-500 text-white">
                              {r.suggested.length} flagged
                            </Badge>
                          )}
                        </div>
                        {hasSuggestions && !r.error && (
                          <p className="text-[11px] text-amber-700/90 dark:text-amber-300/90 mt-0.5">
                            Suggest excluding: {r.suggested.map((k) => labels[k] || k).join(' · ')}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex flex-row items-center justify-between border-t pt-3">
          <p className="text-xs text-muted-foreground">
            {hasResults && (
              <>
                {approvedWithSuggestions.length} of {Object.values(results).filter((r) => r.suggested.length > 0).length} flagged row{approvedWithSuggestions.length === 1 ? '' : 's'} approved
              </>
            )}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => handleOpenChange(false)} data-testid="batch-autodetect-close-btn">
              <X className="h-4 w-4 mr-1" /> Close
            </Button>
            {hasResults && (
              <Button
                onClick={apply}
                disabled={applying || approvedWithSuggestions.length === 0}
                className="bg-amber-500 text-white hover:bg-amber-600"
                data-testid="batch-autodetect-apply-btn"
              >
                {applying
                  ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Applying…</>
                  : <><Check className="h-4 w-4 mr-1" /> Apply to {approvedWithSuggestions.length} emoji{approvedWithSuggestions.length === 1 ? '' : 's'}</>}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BatchAutoDetectDialog;
